/**
 * Stream-status display helper — shared, framework-free.
 *
 * The Portal shows a per-stream health chip in several places (CameraOverview,
 * RealTimeDashboard / CurrentLiveVisitorsData, FlowOutput). Two kinds of edge
 * device feed it over the same socket.io tunnel:
 *
 *   - Old devices  : emit only `heartbeat {streamId, heartbeat}`. We infer
 *                    Healthy (fresh heartbeat) / Down (watchdog expired).
 *   - New devices  : ALSO run the stream-supervisor, which emits a richer
 *                    `stream_status {streamId, status, faultOwner, detail,
 *                    since, action}` with fault attribution and a self-healing
 *                    action.
 *
 * `paused` is normally DB-driven (CameraStreams.StreamStatus === false) and
 * decided on the frontend; the supervisor can also send status:'paused' during
 * a detections toggle, which we map the same way.
 *
 * deriveStreamChip() collapses all of that into one chip and is the single place
 * the old/new precedence lives, so the components don't each reimplement it.
 *
 * Words shown on screen (label, guidance, duration) come from the `streams.`
 * translations at call time; the English `label` below stays as the reference
 * text. Logic decides on `state`, never on a label.
 */

import { i18n } from '@/i18n'

const t = (key, params) => i18n.global.t(key, params)

// How long a received `stream_status` is trusted before we fall back to the
// heartbeat watchdog. The supervisor re-emits at least every ~10s while a stream
// runs, so 45s tolerates a missed poll/emit but still degrades gracefully if the
// supervisor dies or the device is an old (heartbeat-only) one.
export const STREAM_STATUS_FRESH_MS = 45000

// Supervisor `status` -> chip presentation. `tone` maps to the existing chip CSS
// classes (chip-ok / chip-warn / chip-muted). Recovering states are amber: they
// are a real (if self-healing) problem, not a neutral/idle condition.
export const STREAM_STATUS_UI = {
  healthy:               { state: 'healthy',        label: 'Healthy',        tone: 'ok',    icon: 'bi-check2' },
  camera_offline:        { state: 'camera_offline', label: 'Camera offline', tone: 'warn',  icon: 'bi-camera-video-off' },
  processing_recovering: { state: 'recovering',     label: 'Recovering…',    tone: 'warn',  icon: 'bi-arrow-repeat' },
  capture_recovering:    { state: 'recovering',     label: 'Recovering…',    tone: 'warn',  icon: 'bi-arrow-repeat' },
  degraded:              { state: 'degraded',       label: 'Degraded',       tone: 'warn',  icon: 'bi-exclamation-triangle-fill' },
  paused:                { state: 'paused',         label: 'Paused',         tone: 'muted', icon: 'bi-slash-circle' },
}

const TONE_CLASS = {
  ok:    'chip-ok',
  warn:  'chip-warn',
  muted: 'chip-muted',
}

// Human label per self-healing action (shown so the user sees we're handling it).
// Values are translation keys under `streams.action.`.
const ACTION_LABEL = {
  reconnecting: 'reconnecting',
  restarting:   'restarting',
  escalated:    'escalated',
}

// Fallback for an unknown future supervisor state — surface it rather than hide it.
const UNKNOWN_RICH = { state: 'degraded', label: 'Degraded', tone: 'warn', icon: 'bi-exclamation-triangle-fill' }

/**
 * Derive the stream-health chip for one stream.
 *
 * @param {Object}  p
 * @param {boolean} p.dbStreamStatus  CameraStreams.StreamStatus (detections on/off).
 * @param {Object|null} p.richStatus  last `stream_status` payload for this stream, or null.
 * @param {number|null} p.richStatusAt  Date.now() when richStatus was received, or null.
 * @param {boolean} p.heartbeatAlive   current heartbeat-watchdog value for this stream.
 * @param {number}  [p.now=Date.now()] injected for testability.
 * @returns {{state, label, tone, toneClass, icon, detail, faultOwner, action,
 *            guidance, durationText, tooltip}}
 */
export function deriveStreamChip({ dbStreamStatus, richStatus, richStatusAt, heartbeatAlive, now = Date.now() }) {
  // 1. Detections off -> Paused (DB-driven, both device generations).
  if (!dbStreamStatus) {
    return chip(STREAM_STATUS_UI.paused, { detail: null, faultOwner: null, action: null, durationText: '' })
  }

  // 2. A fresh supervisor status -> trust the rich state (new device).
  const richFresh = richStatus && richStatusAt != null && (now - richStatusAt) < STREAM_STATUS_FRESH_MS
  if (richFresh) {
    const ui = STREAM_STATUS_UI[richStatus.status] || UNKNOWN_RICH
    return chip(ui, {
      detail: richStatus.detail || null,
      faultOwner: richStatus.faultOwner || null,
      action: richStatus.action || null,
      durationText: formatSince(richStatus.since, now),
    })
  }

  // 3. Fall back to today's heartbeat watchdog (old device, or supervisor silent).
  return heartbeatAlive
    ? chip(STREAM_STATUS_UI.healthy, { detail: null, faultOwner: null, action: null, durationText: '' })
    : chip({ state: 'down', label: 'Down', tone: 'warn', icon: 'bi-exclamation-triangle-fill' },
        { detail: null, faultOwner: null, action: null, durationText: '' })
}

function chip(ui, { detail, faultOwner, action, durationText }) {
  const guidance = deriveGuidance(ui, faultOwner, action)
  const tooltip = [guidance, detail, durationText ? t('streams.forDuration', { duration: durationText }) : null]
    .filter(Boolean).join(' · ') || null
  return {
    state: ui.state,
    label: t(`streams.status.${ui.state}`),
    tone: ui.tone,
    toneClass: TONE_CLASS[ui.tone] || 'chip-muted',
    icon: ui.icon,
    detail: detail || null,
    faultOwner: faultOwner || null,
    action: action || null,
    guidance,
    durationText: durationText || '',
    tooltip,
  }
}

// Short, store-actionable guidance shown next to the chip.
// Decides on ui.state, never on the label: labels get translated.
function deriveGuidance(ui, faultOwner, action) {
  if (ui.state === 'camera_offline') {
    return faultOwner === 'customer' ? t('streams.guidance.checkCamera') : t('streams.guidance.supportNotified')
  }
  if (ui.state === 'recovering') {
    return ACTION_LABEL[action] ? t(`streams.action.${ACTION_LABEL[action]}`) : t('streams.status.recovering')
  }
  if (action && ACTION_LABEL[action]) {
    return t(`streams.action.${ACTION_LABEL[action]}`)
  }
  return null
}

// Compact "since" -> duration text, e.g. 200 -> "3m", 3900 -> "1h 5m".
// `since` is epoch SECONDS (as the supervisor sends it); returns '' if absent/invalid.
export function formatSince(since, now = Date.now()) {
  if (since == null) return ''
  const secs = Math.floor(now / 1000 - Number(since))
  if (!Number.isFinite(secs) || secs < 0) return ''
  if (secs < 60) return t('streams.since.seconds', { s: secs })
  const mins = Math.floor(secs / 60)
  if (mins < 60) return t('streams.since.minutes', { m: mins })
  const hrs = Math.floor(mins / 60)
  const remMins = mins % 60
  return remMins ? t('streams.since.hoursMinutes', { h: hrs, m: remMins }) : t('streams.since.hours', { h: hrs })
}
